<template>
  <section class="main">
    <h1 class="intro">
      <template v-if="!isMobile">
        {{
          $t("THIS_COMPUTER_IS_LINKED_TO_APPDB", {
            email: (devices[0] || {}).email,
          })
        }}
      </template>
      <template v-else>
        {{
          $t("THIS_DEVICE_IS_LINKED_TO_APPDB", {
            email: (devices[0] || {}).email,
          })
        }}
      </template>
    </h1>
    <p class="instruction">
      {{ $t("CLICK_DEVICE_TO_SWITCH") }}
    </p>
    <div class="row">
      <div class="devices">
        <div
          class="device"
          v-for="device in devices"
          :class="{
            device_active: device.lt === $store.getters.lt,
            device_disabled: deviceRemoving === device.lt,
          }"
          :key="device.lt"
          @click="switchDeviceMobile(device.lt, $event)"
        >
          <div class="device__img-wrapper">
            <img class="device__img" :src="device.icon" />
          </div>
          <div class="device__desc">
            <span class="device__title">
              {{ device.name }}
              <span v-if="device.pro" class="device__pro">Pro</span>
              <span v-if="device.plus" class="device__pro">Plus</span>
              <a
                v-if="device.lt !== $store.getters.lt"
                ref="removeDevice"
                href="#"
                class="device__remove"
                @click.prevent="removeDevice(device.lt)"
                >{{
                  deviceRemoving === device.lt
                    ? $t("APP_REMOVING")
                    : $t("REMOVE")
                }}</a
              >
            </span>
            <span class="device__info"
              >{{ device.title }})&nbsp;<span
                class="device__jail"
                :class="{
                  device__jail_active:
                    device.jailbreak === 'JAILBROKEN' &&
                    device.jailbreakPrefix !== '? ',
                }"
              >{{ device.jailbreakPrefix + $t(device.jailbreak) }}</span
              ></span
            >
          </div>
          <div class="device__switch" v-if="device.lt !== $store.getters.lt">
            <button
              class="device__button"
              @click.prevent="switchDevice(device.lt)"
            >
              {{ $t("SWITCH") }}
            </button>
            <div class="device__switch-text">{{ $t("TO_THIS_DEVICE") }}</div>
          </div>
        </div>
      </div>
      <div class="link">
        <h2 class="link__title">{{ $t("LINK_NEW_DEVICE") }}<br/><a href="https://www.youtube.com/watch?v=Tp3chHFX0NE" target="_blank">{{ $t('VIDEO_TUTORIAL') }}</a></h2>
        <p class="link__text">
          {{ $t("IF_YOU_NEED_TO_LINK_NEW_DEVICE_HERES_CODE") }}
        </p>
        <span class="link__code">{{ code }}</span>
        <qrcode class="link__qr" :background="qrCodeBg" :value="`https://a.appdb.to/add/?${code}`" />
        <h2 class="link__title">{{ $t("TO_LINK_APPLE_TV") }}</h2>
        <ul
          class="link__list"
          v-html="
            $t('LINK_APPLE_TV_STEPS', {
              link: `https://a.appdb.to/add/?${code}`,
            })
          "
        ></ul>
      </div>
    </div>
  </section>
</template>

<script>
import isItselfLinkable from '@/utils/isItselfLinkable';
import QrcodeVue from 'qrcode.vue';
import { mapState } from 'vuex';

export default {
  components: { qrcode: QrcodeVue },
  data() {
    return {
      title: 'My devices',
      code: '',
      isMobile: isItselfLinkable(),
      deviceRemoving: null,
    };
  },
  computed: {
    ...mapState(['theme']),
    qrCodeBg() {
      return this.theme && getComputedStyle(document.documentElement).getPropertyValue('--secondary-bg');
    },
    devices() {
      return this.$store.getters.devices;
    },
    deviceStr() {
      return isItselfLinkable() ? 'iPhone/iPad/iPod' : 'PC/Mac';
    },
  },
  methods: {
    switchDevice(lt) {
      this.$store.commit('lt', lt);
      this.$store.commit('setConfig', {});
      this.$store.dispatch('getConfig');
      this.getCode();
    },
    switchDeviceMobile(lt, e) {
      if (window.innerWidth >= 1250 || this.$refs.removeDevice.some(el => el === e.target)) {
        return;
      }
      this.switchDevice(lt);
    },
    removeDevice(lt) {
      this.$confirm(this.$t('DEVICE_REMOVAL_WARNING'))
        .then(() => {
          this.deviceRemoving = lt;
        })
        .then(() => this.$api.unlinkDevice(lt))
        .then(() => this.$store.dispatch('getDevices'))
        .finally(() => {
          this.deviceRemoving = null;
        });
    },
    getCode() {
      this.$api.getLinkCode().then((code) => {
        this.code = code;
      });
    },
  },
  created() {
    this.getCode();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: block;
  flex-grow: 1;
}

.instruction {
  display: none;

  @include notebook {
    display: block;
    font-size: 12px;
    margin-bottom: 15px;
    padding: 0 10px;
  }
}

.row {
  display: flex;

  @include notebook {
    flex-direction: column;
  }
}

.devices {
  flex-grow: 1;
}

.device {
  display: flex;
  padding: 10px;
  align-items: center;
  border-radius: 4px;
  @include transition;

  &:not(:first-child) {
    margin-top: 5px;
  }

  &_active {
    box-shadow: var(--my-device-shadow);
    border-color: transparent;
  }

  &_disabled {
    pointer-events: none;
  }

  &__img-wrapper {
    flex: 0 0 44px;
    text-align: center;
    margin-right: 15px;
  }

  &__img {
    height: 45px;
    filter: var(--img-filter);
  }

  &__title {
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }

  &__pro {
    text-transform: uppercase;
    background: $primary;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    padding: 3px 3px 2px;
    margin-left: 5px;
  }

  &__remove {
    color: #fa6565;
    margin-left: 5px;
  }

  &__desc {
    flex-grow: 1;
  }

  &__info {
    line-height: 21px;
  }

  &__jail {
    white-space: nowrap;
    border-radius: 2.5px;
    font-size: 12px;
    padding: 1px 3px 2px;
    line-height: 10px;
    position: relative;
    top: -1px;
    margin-left: 2px;
    background: var(--tertiary-accent);
    color: var(--text-tertiary-accent);

    &_active {
      background: var(--bg-success);
      color: #053305;
    }
  }

  &__switch {
    text-align: center;

    @include notebook {
      display: none;
    }
  }

  &__button {
    background: var(--bg);
    border: 1px solid lighten($primary, 20%);
    border-radius: 2px;
    color: var(--text-primary);
    font-size: 12px;
    padding: 3px 15px;
  }

  &__switch-text {
    color: $text-secondary;
    font-size: 12px;
    margin-top: 2px;
  }
}

.link {
  width: 200px;
  margin-left: 50px;
  background: var(--secondary-bg);
  border-radius: 8px;
  padding: 10px;
  text-align: center;

  @include notebook {
    margin: 15px auto 0;
    width: 100%;
    max-width: 400px;
  }

  &__title {
    font-weight: 500;
    margin-bottom: 10px;
    text-align: left;
  }

  &__text {
    line-height: 15px;
    text-align: left;
  }

  &__code {
    display: block;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 5px;
    margin: 10px 0 5px;
  }

  &__qr {
    margin-top: 10px;
    margin-bottom: 15px;
  }

  &__list {
    list-style: decimal inside;
    text-align: left;

    &::v-deep {
      li {
        margin-bottom: 3px;
        line-height: 18px;
      }

      strong {
        font-weight: bold;
      }
    }
  }
}
</style>
